import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useAttachmentStaging } from '@/hooks/useAttachmentStaging';
import { GenericForm, GenericFormField, GenericSelect } from '@/styles/GenericForm';
import '@/design-system/typography.css';
import '@/design-system/forms.css';
import countries from '@/lib/countries';
import AddressAutocomplete, { AddressSelection } from '@/features/project/info/AddressAutocomplete';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { colors } from '@/design-system/colors';
import { SaveChangesButton, CancelButton, SecondaryButton } from '@/styles/StandardButtons';
import { fetchInvitationStatus, sendCommercialInvitation, noticeAfterSend, statusLabel, type InvitationNotice, type InvitationState } from '../../commercial-invitation';
import { GenericAttachmentsSection, GenericAttachment } from '@/styles/GenericAttachmentsSection';
import { uploadClientAttachments, deleteClientAttachment, fetchClientAttachments, ClientAttachment } from '../store';
import { useUser } from '@/contexts/UserContext';

interface Client {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company_name: string;
  address: string;
  city: string;
  state: string;
  country: string;
  zip_code: string;
  is_active: boolean;
  notes: string;
  created_at: string;
}

interface ClientFormProps {
  isOpen: boolean;
  client: Client | null;
  onClose: () => void;
  /** Saves the contact only. Returns the saved person's id (the invitation starts from it). */
  onSave: (data: any) => Promise<string | null | void>;
  /** Shown by the page after "Send Invitation" (the form closes once the contact is saved). */
  onInvitation?: (notice: InvitationNotice) => void;
  error?: string | null;
}

export function ClientForm({
  isOpen,
  client,
  onClose,
  onSave,
  onInvitation,
  error: externalError,
}: ClientFormProps) {
  const { companyId, user } = useUser();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const originalFormDataRef = useRef<Record<string, any>>({});
  const [invitation, setInvitation] = useState<InvitationState | null>(null);
  useEffect(() => {
    setInvitation(null);
    if (!isOpen || !client?.id) return;
    let cancelled = false;
    fetchInvitationStatus(client.id).then((state) => { if (!cancelled) setInvitation(state); }).catch(() => { /* no status shown: the button still works */ });
    return () => { cancelled = true; };
  }, [isOpen, client?.id]);
  const uploadClientAttachmentsFn = useCallback(async (id: string, files: File[]): Promise<GenericAttachment[]> => {
    if (!companyId || !user?.id) return [];
    return uploadClientAttachments(id, files, companyId, user.id);
  }, [companyId, user]);
  const deleteClientAttachmentFn = useCallback((id: string) => {
    if (!user?.id) return Promise.resolve(false);
    return deleteClientAttachment(id, user.id);
  }, [user]);
  const {
    pendingFiles,
    uploadedFiles: attachments,
    setUploadedFiles: setAttachments,
    handleUpload: handleFileUpload,
    handleRemovePending,
    handleDelete: handleFileDelete,
    resetPending: resetPendingFiles,
  } = useAttachmentStaging<GenericAttachment>({
    entityId: client?.id,
    uploadFn: uploadClientAttachmentsFn,
    deleteFn: deleteClientAttachmentFn,
    newFilesPosition: 'prepend',
    onError: setError,
  });
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);

  // Compute initial values directly from client prop
  // Key by client.id to ensure GenericForm updates when switching between clients
  const initialValues = useMemo(() => {
    if (!isOpen) return {};
    
    const clientId = client?.id || 'new';
    
    const values = client ? {
      first_name: client.first_name || '',
      last_name: client.last_name || '',
      email: client.email || '',
      phone: client.phone || '',
      company_name: client.company_name || '',
      address: client.address || '',
      city: client.city || '',
      state: client.state || '',
      country: client.country || '',
      zip_code: client.zip_code || '',
      is_active: client.is_active !== undefined ? (client.is_active ? 'active' : 'inactive') : 'active',
      notes: client.notes || '',
      _clientId: clientId, // Include ID to force update when switching clients
    } : {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      company_name: '',
      address: '',
      city: '',
      state: '',
      country: '',
      zip_code: '',
      is_active: 'active',
      notes: '',
      _clientId: clientId,
    };
    
    // Initialize formData when form opens
    setFormData(values);
    // Store original form data for unsaved changes detection
    originalFormDataRef.current = JSON.parse(JSON.stringify(values));
    return values;
  }, [isOpen, client?.id]);

  // Update error from external prop
  React.useEffect(() => {
    if (externalError) {
      setError(externalError);
    }
  }, [externalError]);

  // Fetch attachments when client changes
  useEffect(() => {
    if (isOpen && client?.id && companyId) {
      setIsLoadingAttachments(true);
      fetchClientAttachments(client.id, companyId)
        .then((fetchedAttachments) => {
          const genericAttachments: GenericAttachment[] = fetchedAttachments.map((att) => ({
            id: att.id,
            file_name: att.file_name,
            file_type: att.file_type,
            file_size: att.file_size,
            file_url: att.file_url,
            created_at: att.created_at,
            created_by: att.created_by,
            created_by_user: att.created_by_user,
          }));
          setAttachments(genericAttachments);
        })
        .catch((err) => {
          console.error('Error fetching attachments:', err);
          setAttachments([]);
        })
        .finally(() => {
          setIsLoadingAttachments(false);
        });
    } else {
      setAttachments([]);
      resetPendingFiles();
    }
  }, [isOpen, client?.id, companyId, setAttachments, resetPendingFiles]);

  // Helper function to check if form data has changed
  const hasFormChanged = (current: Record<string, any>, original: Record<string, any>): boolean => {
    const allKeys = Array.from(new Set([...Object.keys(current), ...Object.keys(original)]));
    for (const key of allKeys) {
      // Skip internal fields
      if (key === '_clientId') continue;
      
      const currentValue = current[key];
      const originalValue = original[key];
      const currentStr = currentValue == null ? '' : String(currentValue).trim();
      const originalStr = originalValue == null ? '' : String(originalValue).trim();
      if (currentStr !== originalStr) {
        return true;
      }
    }
    return false;
  };

  const handleCloseAttempt = () => {
    const isDirty = hasFormChanged(formData, originalFormDataRef.current);
    if (isDirty) {
      if (window.confirm("Are you sure you want to lose all unsaved progress?")) {
        onClose();
        // Reset form data to original
        setFormData(JSON.parse(JSON.stringify(originalFormDataRef.current)));
      }
    } else {
      onClose();
    }
  };

  const handleFieldChange = (name: string, value: any, updatedFormData: Record<string, any>) => {
    setFormData(updatedFormData);
  };

  // Helper function to match country from API to our countries list
  const matchCountry = (apiCountry: string | undefined): string | undefined => {
    if (!apiCountry) return undefined;
    
    // Try exact match first
    const exactMatch = countries.find(c => c.toLowerCase() === apiCountry.toLowerCase());
    if (exactMatch) return exactMatch;
    
    // Try common variations
    const countryVariations: Record<string, string> = {
      'us': 'United States',
      'usa': 'United States',
      'united states of america': 'United States',
      'uk': 'United Kingdom',
      'united kingdom of great britain and northern ireland': 'United Kingdom',
      'uae': 'United Arab Emirates',
      'south korea': 'South Korea',
      'north korea': 'North Korea',
      'russia': 'Russia',
      'russian federation': 'Russia',
    };
    
    const normalized = apiCountry.toLowerCase().trim();
    if (countryVariations[normalized]) {
      return countryVariations[normalized];
    }
    
    // Try partial match (e.g., "United States" matches "United States of America")
    const partialMatch = countries.find(c => 
      c.toLowerCase().includes(normalized) || normalized.includes(c.toLowerCase())
    );
    if (partialMatch) return partialMatch;
    
    return undefined;
  };

  const handleAddressChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      address: value
    }));
  };

  const handleAddressSelect = (selection: AddressSelection) => {
    const matchedCountry = matchCountry(selection.country);
    setFormData(prev => ({
      ...prev,
      address: selection.addressLine1,
      city: selection.city || prev.city,
      state: selection.state || prev.state,
      zip_code: selection.postalCode || prev.zip_code,
      ...(matchedCountry && { country: matchedCountry }),
    }));
  };


  const handleSubmit = async (data: Record<string, any>, { invite = false }: { invite?: boolean } = {}) => {
    // Validate required fields
    if (!data.first_name?.trim() || !data.last_name?.trim() || !data.email?.trim()) {
      setError('First name, last name, and email are required');
      return;
    }

    // Validate email format
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!emailRegex.test(data.email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Convert is_active back to boolean
      const submitData = {
        ...data,
        is_active: data.is_active === 'active',
      };

      const savedId = await onSave(submitData);
      if (invite) {
        // the contact is saved; now start the commercial flow (never reported as sent unless the email really went out)
        const personId = savedId || client?.id;
        let outcome: InvitationState | null = null;
        let failure: unknown = null;
        try {
          if (!personId) throw new Error('The contact has no id yet');
          outcome = await sendCommercialInvitation(personId);
        } catch (inviteError) {
          failure = inviteError;
        }
        onInvitation?.(noticeAfterSend(outcome, failure, `${data.first_name} ${data.last_name}`.trim() || data.email));
      }
      onClose();
    } catch (err: any) {
      console.error('Error saving client:', err);
      setError(err.message || 'Failed to save client');
    } finally {
      setIsSubmitting(false);
    }
  };

  const clientFields = useMemo<GenericFormField[]>(() => [
    {
      name: 'first_name',
      label: 'First Name',
      type: 'text',
      required: true,
      row: 1,
      colSpan: 1,
      placeholder: 'Enter first name',
    },
    {
      name: 'last_name',
      label: 'Last Name',
      type: 'text',
      required: true,
      row: 1,
      colSpan: 1,
      placeholder: 'Enter last name',
    },
    {
      name: 'email',
      label: 'Email',
      type: 'email',
      required: true,
      row: 2,
      colSpan: 1,
      placeholder: 'Enter email',
    },
    {
      name: 'phone',
      label: 'Phone',
      type: 'text',
      row: 2,
      colSpan: 1,
      placeholder: 'Enter phone number',
    },
    {
      name: 'company_name',
      label: 'Company Name',
      type: 'text',
      row: 3,
      colSpan: 0.5,
      placeholder: 'Enter company name',
    },
    {
      name: 'is_active',
      label: 'Status',
      type: 'select',
      row: 3,
      colSpan: 0.5,
      options: [
        { label: 'Active', value: 'active' },
        { label: 'Inactive', value: 'inactive' },
      ],
      placeholder: 'Select',
    },
    {
      name: 'address',
      label: 'Address',
      type: 'text',
      row: 4,
      colSpan: 2,
      placeholder: 'Enter street address',
      // This field will be replaced with AddressAutocomplete in customLayout
    },
    {
      name: 'city',
      label: 'City',
      type: 'text',
      row: 5,
      colSpan: 0.5,
      placeholder: 'Enter city',
    },
    {
      name: 'state',
      label: 'State',
      type: 'text',
      row: 5,
      colSpan: 0.5,
      placeholder: 'Enter state',
    },
    {
      name: 'country',
      label: 'Country',
      type: 'select',
      row: 5,
      colSpan: 0.5,
      options: countries.map(c => ({ label: c, value: c })),
      placeholder: 'Select',
    },
    {
      name: 'zip_code',
      label: 'ZIP Code',
      type: 'text',
      row: 5,
      colSpan: 0.5,
      placeholder: 'Enter ZIP code',
    },
    {
      name: 'notes',
      label: 'Notes',
      type: 'textarea',
      row: 6,
      colSpan: 2,
      placeholder: 'Enter any additional notes',
    },
  ], []);

  // Group fields by row
  const fieldsByRow = clientFields.reduce((acc, field) => {
    const row = field.row || 999;
    if (!acc[row]) acc[row] = [];
    acc[row].push(field);
    return acc;
  }, {} as Record<number, GenericFormField[]>);

  const sortedRows = Object.keys(fieldsByRow).sort((a, b) => Number(a) - Number(b));

  // Helper to render a field
  const renderField = (field: GenericFormField) => {
    const fieldValue = (formData as Record<string, any>)[field.name] || '';
    if (field.type === 'select') {
      return (
        <GenericSelect
          value={String(fieldValue)}
          onValueChange={(value) => handleFieldChange(field.name, value, { ...formData, [field.name]: value })}
          disabled={field.disabled}
          required={field.required}
          placeholder={field.placeholder}
          contentClassName={field.name === 'country' ? "max-h-[18.75rem] overflow-y-auto" : ""}
          options={field.options || []}
        />
      );
    } else if (field.type === 'textarea') {
      return (
        <Textarea
          id={field.name}
          value={String(fieldValue)}
          onChange={(e) => handleFieldChange(field.name, e.target.value, { ...formData, [field.name]: e.target.value })}
          placeholder={field.placeholder}
          disabled={field.disabled}
        />
      );
    } else {
      return (
        <Input
          id={field.name}
          type={field.type}
          value={String(fieldValue)}
          onChange={(e) => handleFieldChange(field.name, e.target.value, { ...formData, [field.name]: e.target.value })}
          placeholder={field.placeholder}
          className="font-form-input generic-form-field"
          disabled={field.disabled}
        />
      );
    }
  };

  const customLayout = (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit(formData);
      }}
      className="generic-form-container"
      style={{ maxHeight: 'var(--form-max-height-default)' }}
    >
      {/* Header */}
      <div 
        className={cn("generic-form-header", "generic-form-header-default")}
        style={{ backgroundColor: colors.formHeaderFooter }}
      >
        <h2 className="font-form-title">
          {client ? 'Edit Client' : 'Create Client'}
        </h2>
        <button
          type="button"
          onClick={handleCloseAttempt}
          className="generic-close-button"
        >
          <svg className="generic-close-button-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Body */}
      <div className={cn("generic-form-body", "generic-form-body-default")}>
        {error && (
          <div className="generic-form-error">
            {error}
          </div>
        )}
        <div className="generic-form-body-spacing-default">
          {/* Row 1: First Name, Last Name */}
          <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 'var(--field-gap-default)' }}>
            {fieldsByRow[1]?.map((field) => (
              <div key={field.name} className="space-y-2">
                <Label htmlFor={field.name} className="font-form-label">
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </Label>
                {renderField(field)}
              </div>
            ))}
          </div>

          {/* Row 2: Email, Phone */}
          <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 'var(--field-gap-default)' }}>
            {fieldsByRow[2]?.map((field) => (
              <div key={field.name} className="space-y-2">
                <Label htmlFor={field.name} className="font-form-label">
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </Label>
                {renderField(field)}
              </div>
            ))}
          </div>

          {/* Row 3: Company Name, Status */}
          <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 'var(--field-gap-default)' }}>
            {fieldsByRow[3]?.map((field) => (
              <div key={field.name} className="space-y-2">
                <Label htmlFor={field.name} className="font-form-label">
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </Label>
                {renderField(field)}
              </div>
            ))}
          </div>

          {/* Row 4: Address (full width) with AddressAutocomplete */}
          <div className="space-y-2">
            <Label htmlFor="address" className="font-form-label">
              Address
            </Label>
            <AddressAutocomplete
              value={formData.address || ''}
              onChange={handleAddressChange}
              onSelectSuggestion={handleAddressSelect}
              placeholder="Start typing address"
              inputClassName="w-full"
              initialSelected={Boolean(client)}
            />
          </div>

          {/* Row 5: City, State, Country, ZIP Code (1/4 width each) */}
          <div className="grid grid-cols-1 md:grid-cols-4" style={{ gap: 'var(--field-gap-default)' }}>
            {fieldsByRow[5]?.map((field) => (
              <div key={field.name} className="space-y-2">
                <Label htmlFor={field.name} className="font-form-label">
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </Label>
                {renderField(field)}
              </div>
            ))}
          </div>

          {/* Row 6: Notes */}
          <div className="space-y-2">
            {fieldsByRow[6]?.map((field) => (
              <div key={field.name} className="space-y-2">
                <Label htmlFor={field.name} className="font-form-label">
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </Label>
                {renderField(field)}
              </div>
            ))}
          </div>

          {/* Attachments Section */}
          <div className="space-y-2">
            <GenericAttachmentsSection
              attachments={attachments}
              pendingFiles={pendingFiles}
              onUpload={handleFileUpload}
              onDelete={client?.id ? handleFileDelete : undefined}
              onRemovePending={handleRemovePending}
              projectId={null} mode={client ? 'edit' : 'create'}
              disabled={isSubmitting || isLoadingAttachments}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div 
        className={cn("generic-form-footer", "generic-form-footer-default")}
        style={{ backgroundColor: colors.formHeaderFooter }}
      >
        {client?.id && invitation && <span className="mr-auto text-sm text-gray-600" data-testid="invitation-status">{statusLabel(invitation)}</span>}
        <CancelButton onClick={handleCloseAttempt} disabled={isSubmitting} />
        <SecondaryButton type="button" disabled={isSubmitting || invitation?.status === 'accepted'} onClick={() => handleSubmit(formData, { invite: true })}>
          {invitation && ['sent', 'failed', 'expired'].includes(invitation.status) ? 'Resend Invitation' : 'Send Invitation'}
        </SecondaryButton>
        <SaveChangesButton
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </SaveChangesButton>
      </div>
    </form>
  );

  return (
    <GenericForm
      title="Client"
      mode={client ? 'edit' : 'create'}
      fields={clientFields}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onCancel={handleCloseAttempt}
      isOpen={isOpen}
      error={error}
      submitButtonText="Save"
      saving={isSubmitting}
      onFieldChange={handleFieldChange}
      customLayout={customLayout}
    />
  );
}

